import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import './NewsList.css';
import Header from '../components/Header';
import NewsCard from '../components/NewsCard';
import { OrganizationSchema, NewsCollectionSchema, BreadcrumbSchema } from '../components/SchemaMarkup';

const fallbackImage = `${process.env.PUBLIC_URL || ''}/assets/banner.png`;

const NewsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');

  // Fetch news articles
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setIsLoading(true);
        setError('');

        const response = await fetch('/api/news?limit=100');
        const data = await response.json();

        if (!response.ok) {
          setError(data.message || 'Unable to load news articles.');
          return;
        }

        setNews(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Unable to load news articles.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNews();
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('/api/categories');
        const data = await response.json();
        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  // Filter and sort articles
  const filteredNews = useMemo(() => {
    let filtered = [...news];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (article) =>
          article.title.toLowerCase().includes(term) ||
          (article.content && article.content.toLowerCase().includes(term))
      );
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter((article) => article.category_id === parseInt(selectedCategory));
    }

    // Sort
    switch (sortBy) {
      case 'oldest':
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'most-viewed':
        filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
        break;
      case 'highest-rated':
        filtered.sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
        break;
      case 'newest':
      default:
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
    }

    return filtered;
  }, [news, searchTerm, selectedCategory, sortBy]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    if (selectedCategory) params.set('category', selectedCategory);
    if (sortBy !== 'newest') params.set('sort', sortBy);

    setSearchParams(params);
  }, [searchTerm, selectedCategory, sortBy, setSearchParams]);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSortBy('newest');
  };

  const hasActiveFilters = searchTerm || selectedCategory || sortBy !== 'newest';

  return (
    <div className="news-list-page">
      {/* Schema Markup for SEO */}
      <OrganizationSchema />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: `${typeof window !== 'undefined' ? window.location.origin : ''}/` },
          { name: 'News', url: typeof window !== 'undefined' ? window.location.href : '' },
        ]}
      />
      {filteredNews.length > 0 && (
        <NewsCollectionSchema
          articles={filteredNews.map((article) => ({
            title: article.title,
            description: article.content?.substring(0, 160),
            image: article.img || fallbackImage,
            datePublished: article.created_at,
            url: `${typeof window !== 'undefined' ? window.location.origin : ''}/news/${article.slug}`,
            aggregateRating: article.average_rating > 0 ? {
              ratingValue: article.average_rating,
              ratingCount: article.rating_count || 0,
            } : null,
          }))}
        />
      )}

      <Header />

      <main className="news-list-main">
        <div className="news-list-header">
          <div className="news-list-header-content">
            <h1>Latest Driving News & Updates</h1>
            <p>Stay informed with the latest tips, regulations, and driving knowledge</p>
          </div>
        </div>

        {/* Filters Section */}
        <section className="news-list-filters">
          <div className="news-list-filter-container">
            {/* Search */}
            <div className="news-list-search">
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={handleSearch}
                className="news-list-search-input"
                aria-label="Search news articles"
              />
              <span className="news-list-search-icon">🔍</span>
            </div>

            {/* Category Filter */}
            <div className="news-list-filter-group">
              <label htmlFor="category-select">Category:</label>
              <select
                id="category-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="news-list-select"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Filter */}
            <div className="news-list-filter-group">
              <label htmlFor="sort-select">Sort by:</label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="news-list-select"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="most-viewed">Most Viewed</option>
                <option value="highest-rated">Highest Rated</option>
              </select>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button className="news-list-clear-btn" onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        </section>

        {/* Results Info */}
        {!isLoading && (
          <div className="news-list-results-info">
            Showing {filteredNews.length} {filteredNews.length === 1 ? 'article' : 'articles'}
            {searchTerm && ` matching "${searchTerm}"`}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="news-list-loading">
            <div className="news-list-spinner"></div>
            <p>Loading articles...</p>
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <div className="news-list-error">
            <h3>Unable to Load Articles</h3>
            <p>{error}</p>
          </div>
        )}

        {/* Articles Grid */}
        {!isLoading && !error && filteredNews.length > 0 && (
          <div className="news-list-grid">
            {filteredNews.map((article) => (
              <NewsCard key={article.id} article={article} />
            ))}
          </div>
        )}

        {/* No Results State */}
        {!isLoading && !error && filteredNews.length === 0 && (
          <div className="news-list-empty">
            <div className="news-list-empty-icon">📰</div>
            <h3>No Articles Found</h3>
            <p>
              {searchTerm
                ? `No articles match your search "${searchTerm}". Try different keywords.`
                : 'No articles available at the moment. Check back soon!'}
            </p>
            {hasActiveFilters && (
              <button className="news-list-empty-btn" onClick={clearFilters}>
                Clear Filters and Try Again
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default NewsList;
